<hx-form-group [error]="_showErrors()" [class.hx-form-group-horizontal]="orientation() === 'horizontal'">
  <!-- Label -->
  @if (label()) {
    <hx-label
      [description]="label()"
      [required]="requiredInput()"
      [subtitle]="subtitle()"
      [fontWeight]="labelFontWeight()"
      [fontSize]="labelFontSize()"
      [subtitleFontWeight]="subtitleFontWeight()"
      [subtitleFontSize]="subtitleFontSize()"
      [color]="labelColor()"
      [subtitleColor]="subtitleColor()"
      [tooltip]="tooltip()"
      [tooltipPosition]="tooltipPosition()"
      [tooltipColor]="tooltipColor()"></hx-label>
  }

  <!-- Input wrapper -->
  <div class="hx-input-wrapper">
    <!-- Start slot for icons/buttons (T055, T057) -->
    <div class="hx-input-start">
      @if (startIcon()) {
        <hx-icon [fontIcon]="startIcon() ?? ''" [inline]="true" />
      }
    </div>

    @if (_showNativePickerPlaceholder()) {
      <span class="hx-input-native-placeholder">{{ placeholder() }}</span>
    }

    <!-- Input or Textarea element based on multiline mode -->
    @if (multiline()) {
      <textarea
        #textareaElement
        class="hx-input-element"
        [class.hx-input-element--with-dictation]="showDictation()"
        [attr.id]="id()"
        [attr.aria-disabled]="disabledState()"
        [attr.aria-invalid]="_showErrors()"
        [attr.aria-label]="ariaLabelInput()"
        [attr.aria-multiline]="true"
        [attr.aria-readonly]="readonly()"
        [attr.aria-required]="requiredInput()"
        [attr.autofocus]="autofocus() ? '' : null"
        [attr.maxlength]="maxlength()"
        [attr.minlength]="minlength()"
        [disabled]="disabledState()"
        [placeholder]="placeholder()"
        [readonly]="readonly()"
        [required]="requiredInput()"
        [rows]="autoExpand() ? minRows() : rows()"
        [style.resize]="autoExpand() ? 'none' : resize()"
        (blur)="_handleBlur()"
        (focus)="_handleFocus()"
        (input)="_handleInput($event)"></textarea>
    } @else {
      <input
        #inputElement
        class="hx-input-element"
        [class.hx-input-native-picker-empty]="_showNativePickerPlaceholder()"
        hxEllipsisTooltip
        [hxEllipsisTooltip]="_internalValue()"
        [ellipsisPosition]="ellipsisTooltipPosition()"
        [attr.id]="id()"
        [attr.aria-disabled]="disabledState()"
        [attr.aria-invalid]="_showErrors()"
        [attr.aria-label]="ariaLabelInput()"
        [attr.aria-readonly]="readonly()"
        [attr.aria-required]="requiredInput()"
        [attr.autocomplete]="autocomplete()"
        [attr.autofocus]="autofocus() ? '' : null"
        [attr.max]="_effectiveMax()"
        [attr.maxlength]="maxlength()"
        [attr.min]="_effectiveMin()"
        [attr.minlength]="minlength()"
        [attr.pattern]="pattern()"
        [disabled]="disabledState()"
        [placeholder]="type() === 'timeRange' ? '--:-- -- \u2013 --:-- --' : placeholder()"
        [readonly]="type() === 'timeRange' || readonly()"
        [required]="requiredInput()"
        [style.cursor]="type() === 'timeRange' ? 'pointer' : null"
        [type]="_inputType()"
        (click)="_handleTimeRangeFieldClick($event)"
        (blur)="_handleBlur()"
        (focus)="_handleFocus()"
        (input)="_handleInput($event)" />
    }

    <!-- End slot for icons/buttons (T056, T058) -->
    <div class="hx-input-end">
      @if (type() === 'timeRange') {
        <button
          type="button"
          (click)="_toggleTimeRangePicker($event)"
          aria-label="Toggle time range picker"
          tabindex="-1">
          <hx-icon svgIcon="clock-history" [inline]="true" />
        </button>
      } @else if (type() === 'password') {
        <button
          type="button"
          (click)="_togglePasswordVisibility()"
          [attr.aria-label]="_showPassword() ? 'Hide password' : 'Show password'"
          tabindex="-1">
          <hx-icon [svgIcon]="_passwordToggleIcon()" />
        </button>
      } @else if (_resolvedEndIcon()) {
        <hx-icon [svgIcon]="_resolvedEndIcon() ?? ''" [inline]="true" />
      }
    </div>

    @if (showDictation()) {
      <button
        hxIconButton
        type="button"
        variant="ghost"
        context="neutral"
        size="xs"
        class="hx-input-dictation-button"
        [class.hx-input-dictation-button--listening]="dictationState() === 'listening'"
        [class.hx-input-dictation-button--error]="dictationState() === 'error'"
        [svgIcon]="dictationState() === 'listening' ? 'ccs-mic-fill' : 'ccs-mic'"
        [disabled]="disabledState() || readonly()"
        [attr.aria-pressed]="dictationState() === 'listening'"
        [attr.aria-label]="dictationButtonLabel()"
        [attr.title]="dictationButtonLabel()"
        (pointerenter)="_onDictationButtonWarmUp()"
        (focus)="_onDictationButtonWarmUp()"
        (click)="_onDictationButtonClick($event)"></button>
    }
  </div>

  <!-- CDK Overlay template for time range picker - rendered outside component DOM tree -->
  <ng-template #timeRangePickerTpl>
    <div class="hx-input-time-range-picker" role="dialog" aria-label="Select time range">
      <div class="hx-input-time-range-picker__header">Select Time Range</div>

      <div class="hx-input-time-range-picker__row">
        <span class="hx-input-time-range-picker__label">Start</span>
        <input
          class="hx-input-time-range-picker__time-input"
          [id]="id() + '-time-range-start'"
          type="time"
          [value]="_timeRangeStart()"
          (input)="_onTimeRangeStartChange($event)" />
      </div>

      <div class="hx-input-time-range-picker__row">
        <span class="hx-input-time-range-picker__label">End</span>
        <input
          class="hx-input-time-range-picker__time-input"
          [id]="id() + '-time-range-end'"
          type="time"
          [value]="_timeRangeEnd()"
          (input)="_onTimeRangeEndChange($event)" />
      </div>

      <div class="hx-input-time-range-picker__actions">
        <button
          class="hx-input-time-range-picker__btn hx-input-time-range-picker__btn--clear"
          type="button"
          (click)="_clearTimeRange()">
          Clear
        </button>
        <button
          class="hx-input-time-range-picker__btn hx-input-time-range-picker__btn--apply"
          type="button"
          (click)="_applyTimeRange()">
          Apply
        </button>
      </div>
    </div>
  </ng-template>

  <!-- Hint Text -->
  @if (hint()) {
    <span hx-hint>{{ hint() }}</span>
  }

  <!-- Error Messages - Uses _showErrors signal for synchronous updates -->
  <!-- Priority-based error display: required → format → length → range → custom -->
  @if (_showErrors() && !hideErrorMessage()) {
    <span hx-error>
      @if (_validationState.errors()?.['required']) {
        {{ _getErrorMessage('required') }}
      } @else if (_validationState.errors()?.['email']) {
        {{ _getErrorMessage('email') }}
      } @else if (_validationState.errors()?.['pattern']) {
        {{ _getErrorMessage('pattern') }}
      } @else if (_validationState.errors()?.['alphabetical']) {
        {{ _getErrorMessage('alphabetical') }}
      } @else if (_validationState.errors()?.['numerical']) {
        {{ _getErrorMessage('numerical') }}
      } @else if (_validationState.errors()?.['specialCharacter']) {
        {{ _getErrorMessage('specialCharacter') }}
      } @else if (_validationState.errors()?.['minlength']) {
        {{ _getErrorMessage('minlength') }}
      } @else if (_validationState.errors()?.['maxlength']) {
        {{ _getErrorMessage('maxlength') }}
      } @else if (_validationState.errors()?.['min']) {
        {{ _getErrorMessage('min') }}
      } @else if (_validationState.errors()?.['max']) {
        {{ _getErrorMessage('max') }}
      } @else if (_validationState.errors()?.['negativeValue']) {
        {{ _getErrorMessage('negativeValue') }}
      } @else if (_validationState.errors()?.['timeRange']) {
        {{ _getErrorMessage('timeRange') }}
      } @else if (_validationState.errors() && customErrorMessage()) {
        {{ customErrorMessage() }}
      }
    </span>
  }
</hx-form-group>
